import React, {
  useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import capitalize from 'lodash/capitalize';
import find from 'lodash/find';
import filter from 'lodash/filter';
import styled from 'styled-components';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import {
  Button,
  ButtonVariant,
  Icon,
  IconName,
  Modal,
  Spinner,
  useSnackBar,
} from '@usealloy/component-library';

import FileUploadRow from '~/documents/FileUploadRow';
import {
  ColumnHeader,
} from '~/documents/DocumentUploadModalStyles';
import {
  ChooseFileLabel,
  ChooseFileInput,
  TargetDropBox,
} from '~/components/inputs/ChooseFileInputStyles';
import { handleFileChange, handleUploadDocument, updateFiles } from '~/documents/documentHelper';

const RunEvalDocumentUploadPropTypes = {
  keyValue: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  apiUrl: PropTypes.string.isRequired,
  documentTypes: PropTypes.arrayOf(PropTypes.shape({
    document_type_id: PropTypes.number,
    type: PropTypes.string,
  })).isRequired,
  fillForm: PropTypes.func.isRequired,
  agentEmail: PropTypes.string.isRequired,
};

const CustomSpan = styled.span`
  margin-left: 4px;
  display: inline-block;
`;

const RunEvalDocumentUpload = ({
  keyValue,
  isOpen,
  toggleModal,
  fillForm,
  apiUrl,
  documentTypes,
  agentEmail,
}) => {
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const defaultFileType = find(documentTypes, { type: 'other' });
  const form = useRef(null);

  const handleDragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleOpenModal = () => {
    toggleModal(true);
  };

  const handleFileDrop = item => {
    if (item) {
      const uploaded = item.files;
      updateFiles(defaultFileType, uploaded, setFiles);
    }
  };

  const [, drop] = useDrop(() => ({
    accept: [NativeTypes.FILE],
    drop(item) {
      handleFileDrop(item);
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [handleFileDrop]);


  const fillFormData = tokens => {
    /**
     * Doc upload modal doesn't limit uploads in the run eval form, and this component
     * is only used for `document_token_front` and `document_token_back` fields, so this
     * just assumes that the first doc uploaded is the token that matters
     */
    const token = tokens?.[0];
    setUploading(false);
    toggleModal(false);
    fillForm(token);
  };

  const addSnackBar = useSnackBar();

  const submitForm = event => handleUploadDocument({
    event,
    typhonDomain: apiUrl,
    setUploading,
    documentTypes,
    files,
    onClose: () => { toggleModal(false); },
    successHandler: fillFormData,
    agentEmail,
    addSnackBar,
  });

  return (
    <CustomSpan>
      <Button
        className="flex-wrap circle"
        id={`upload-document${keyValue}`}
        onClick={handleOpenModal}
        type="button"
      >
        <Icon icon={IconName.AddDocument} />
      </Button>

      <Modal
        id={`document-upload-modal-${keyValue}`}
        header={`Upload ${capitalize(keyValue)} Document`}
        isOpen={isOpen}
        onClose={toggleModal}
        primaryAction={(
          files.length > 0 && (
            <Button
              variant={ButtonVariant.primary}
              type="submit"
              disabled={uploading}
              onClick={submitForm}
            >
              {uploading ? <Spinner color="white" /> : <Icon icon={IconName.UploadDocument} color="white" />}
              {' Upload'}
            </Button>
          )
        )}
      >
        <form
          id={`document-upload-form-${keyValue}`}
          ref={form}
          onSubmit={files.length === 0 ? () => {} : submitForm}
        >
          <div className="form-group header-24">
            <TargetDropBox
              className={isDragging ? 'drag-over' : ''}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              ref={drop}
            >
              <Icon
                icon={IconName.AddDocument}
                size="xl"
                color="#d9d9d9"
              />
              <span>Drop your file here, or </span>
              <ChooseFileLabel htmlFor="document-upload" className="file">
                browse files
              </ChooseFileLabel>
              <ChooseFileInput
                id="document-upload"
                className="file"
                type="file"
                multiple
                name="document_upload"
                formEncType="multipart/form-data"
                onChange={event => handleFileChange(defaultFileType, event, setFiles)}
                onClick={e => { e.target.value = null; }}
              />
            </TargetDropBox>
          </div>

          <table>
            <thead>
              <tr>
                <ColumnHeader>File Name</ColumnHeader>
                <ColumnHeader>Type</ColumnHeader>
                <ColumnHeader>Notes</ColumnHeader>
                <ColumnHeader />
              </tr>
            </thead>
            <tbody>
              {/* Generate file row for each file in list */}
              {files.map(file => (
                <FileUploadRow
                  key={file.fileObj.name}
                  file={file}
                  documentTypes={documentTypes}
                  removeFile={f => setFiles(
                    prev => filter(prev, x => x.fileObj.name !== f.fileObj.name),
                  )}
                  updateFile={(old, updated) => setFiles(prev => prev
                    .map(f => (f.fileObj.name === old.fileObj.name ? updated : f)))}
                />
              ))}
            </tbody>
          </table>
        </form>
      </Modal>
    </CustomSpan>
  );
};

RunEvalDocumentUpload.propTypes = RunEvalDocumentUploadPropTypes;

export default RunEvalDocumentUpload;
